import { IDraggableSidebarItem } from "../../controls";
import {
  ArsTableSidebarItemAnalysisMethod,
  ArsTableSidebarItemOperation,
  type ArsTableSidebarItemData,
} from "./interface";

export interface OperationSidebarItemProps {
  item: IDraggableSidebarItem<ArsTableSidebarItemData>;
  selected: boolean;
}

/**
 * Custom sidebar item renderer for analysis methods and operations.
 * Renders analysis methods with bold styling and operations with their operation details.
 *
 * @param props - Props containing the sidebar item and its selected state
 * @param props.item - Sidebar item with discriminated union data (analysis method or operation)
 * @param props.selected - Whether the item is currently selected
 * @returns Analysis method or operation display element
 */
export const OperationSidebarItem = ({ item, selected }: OperationSidebarItemProps) => {
  const isMethod = item.data.kind === "analysisMethod";
  const isOperation = item.data.kind === "operation";
  // Type assertion needed due to discriminated union limitation with generic components
  const analysisMethod = isMethod
    ? (item.data as ArsTableSidebarItemAnalysisMethod).dropItem
    : null;
  const operation = isOperation ? (item.data as ArsTableSidebarItemOperation).dropItem : null;

  return (
    <div
      className={`px-2 py-1 border rounded w-100 ${selected ? "border-primary bg-primary-subtle" : ""}`}
      style={{ cursor: "grab" }}
    >
      {isMethod ? (
        <>
          <div className="fw-bold">{item.label}</div>
          {analysisMethod && (
            <small className="text-muted">Analysis method • {analysisMethod.name}</small>
          )}
        </>
      ) : (
        <>
          <div className="fw-semibold">{item.label}</div>
          {operation && (
            <small className="text-muted">
              {operation.name} {operation.label && `• ${operation.label}`}
            </small>
          )}
        </>
      )}
    </div>
  );
};

OperationSidebarItem.displayName = "OperationSidebarItem";
